import { useContext, useMemo } from 'react'
import { Box, Button, Link, Typography } from '@mui/material'
import { Form, Formik } from 'formik'
import { useSnackbar } from 'notistack'

import FormikChipToggleButton from '../../../../components/formik-chip-toggle-button'
import FormikDropdown from '../../../../components/formik-dropdown'
import FormikPreferencesToggleButton from '../../../../components/formik-preferences-toggle-button'
import Subsection from '../../../../components/subsection'
import { useHost } from '../../../../providers/app-provider'
import { useAuthentication } from '../../../../providers/auth-provider'
import Api from '../../../../utils/api'
import cachedTimezoneList from '../../../../utils/timezoneData'
import PeopleContext from '../../context'
import useStyles from '../../index.styles'

import NotificationPreference from './notification-preference'

interface FormValues {
  notification: string[]
  language: string
  timezone: string
  dateFormat: string
  timeFormat: string
  tempUnits: string
}

const languageMapping: Record<string, string> = {
  'en-US': 'English (United States)',
  'de-DE': 'Deutsch',
  'fr-CA': 'Français (Canada)',
}

const PreferencesContent = ({ isEditing, setIsEditing }: { isEditing: boolean; setIsEditing: React.Dispatch<React.SetStateAction<boolean>> }) => {
  const classes = useStyles

  const { hasInternalUserAccess } = useAuthentication()
  const { user, setUser, loading } = useContext(PeopleContext)
  const host = useHost()

  const { enqueueSnackbar } = useSnackbar()

  let textPreference: PreferenceStateType = 'loading'
  if (!loading && user?.preferences?.notification?.includes('sms')) textPreference = 'on'
  if (!loading && !user?.preferences?.notification?.includes('sms')) textPreference = 'off'

  let phonePreference: PreferenceStateType = 'loading'
  if (!loading && user?.preferences?.notification?.includes('phone')) phonePreference = 'on'
  if (!loading && !user?.preferences?.notification?.includes('phone')) phonePreference = 'off'

  let emailPreference: PreferenceStateType = 'loading'
  if (!loading && user?.preferences?.notification?.includes('email')) emailPreference = 'on'
  if (!loading && !user?.preferences?.notification?.includes('email')) emailPreference = 'off'

  let pushPreference: PreferenceStateType = 'loading'
  if (!loading && user?.preferences?.notification?.includes('push')) pushPreference = 'on'
  if (!loading && !user?.preferences?.notification?.includes('push')) pushPreference = 'off'

  const language = user?.language ? languageMapping[user.language] : (user?.language ?? '')

  const timezone = cachedTimezoneList().find((zone) => zone.tzCode === user?.information?.timezone)
  const timeZoneHeader = timezone?.header
  const timeZoneSubtitle = timezone?.subtitle

  let dateFormat = ''
  if (user?.preferences?.dateFormat === 'MM-DD-YYYY') dateFormat = 'MM/DD/YYYY'
  else if (user?.preferences?.dateFormat === 'DD.MM.YYYY') dateFormat = 'DD.MM.YYYY'

  let timeFormat = ''
  if (user?.preferences?.timeFormat === '12hr') timeFormat = '12 HR'
  else if (user?.preferences?.timeFormat === '24hr') timeFormat = '24 HR'

  let tempUnits = ''
  if (user?.preferences?.tempUnits === 'degF') tempUnits = 'Fahrenheit'
  else if (user?.preferences?.tempUnits === 'degC') tempUnits = 'Celsius'

  const initialValues = useMemo(
    () => ({
      notification: user?.preferences?.notification ?? [],
      language: user?.language && Object.keys(languageMapping).includes(user.language) ? user.language : '',
      timezone: timezone?.tzCode ?? '',
      dateFormat: user?.preferences?.dateFormat ?? '',
      timeFormat: user?.preferences?.timeFormat ?? '',
      tempUnits: user?.preferences?.tempUnits ?? '',
    }),
    [
      timezone?.tzCode,
      user?.preferences?.dateFormat,
      user?.language,
      user?.preferences?.notification,
      user?.preferences?.tempUnits,
      user?.preferences?.timeFormat,
    ]
  )

  const onSubmit = async (values: FormValues) => {
    if (user) {
      const res = await Api.put(`${host}/api/users/${user.id}`, {
        ...user,
        information: {
          ...user.information,
          timezone: values.timezone ? values.timezone : (user.information?.timezone ?? ''),
        },
        preferences: {
          ...user.preferences,
          notification: values.notification,
          dateFormat: values.dateFormat,
          timeFormat: values.timeFormat,
          tempUnits: values.tempUnits,
        },
        language: values.language,
      })

      if (res.ok) {
        setUser(res.body)
        setIsEditing(false)
        enqueueSnackbar('User updated successfully.')
      } else {
        enqueueSnackbar('Could not update user.', { variant: 'error' })
      }
    }
  }

  if (isEditing)
    return (
      <div style={classes.subsectionContainer}>
        <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize={true}>
          {({ isSubmitting, dirty }) => (
            <Form>
              <Subsection
                header="Notifications"
                content={
                  <FormikPreferencesToggleButton
                    name="notification"
                    options={[
                      { value: 'sms', label: 'Text' },
                      { value: 'phone', label: 'Phone' },
                      { value: 'email', label: 'Email' },
                      { value: 'push', label: 'Push', disabled: true, disabledTooltip: 'Selection by user, in app only' },
                    ]}
                  />
                }
                loading={loading}
              />
              <Subsection
                header="Language"
                content={
                  <Box sx={classes.formikDropdownContainer}>
                    <FormikDropdown
                      name="language"
                      label="Language"
                      required={false}
                      items={[
                        { id: 'en-US', name: 'English (United States)' },
                        { id: 'de-DE', name: 'Deutsch' },
                        { id: 'fr-CA', name: 'Français (Canada)' },
                      ]}
                    />
                  </Box>
                }
                loading={loading}
              />
              <Subsection
                header="Timezone"
                content={
                  <Box sx={classes.formikDropdownContainer}>
                    <FormikDropdown
                      name="timezone"
                      label="Time Zone"
                      required={false}
                      items={cachedTimezoneList().map((zone) => ({ id: zone.tzCode, name: `${zone.header} (${zone.subtitle})` }))}
                    />
                  </Box>
                }
                loading={loading}
              />
              <Subsection
                header="Date Format"
                content={
                  <FormikChipToggleButton
                    name="dateFormat"
                    options={[
                      { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY' },
                      { value: 'DD.MM.YYYY', label: 'DD-MM-YYYY' },
                    ]}
                    width="141px"
                  />
                }
                loading={loading}
              />
              <Subsection
                header="Time Format"
                content={
                  <FormikChipToggleButton
                    name="timeFormat"
                    options={[
                      { value: '12hr', label: '12HR' },
                      { value: '24hr', label: '24HR' },
                    ]}
                    width="141px"
                  />
                }
                loading={loading}
              />
              <Subsection
                header="Temperature"
                content={
                  <FormikChipToggleButton
                    name="tempUnits"
                    options={[
                      { value: 'degF', label: 'Fahrenheit' },
                      { value: 'degC', label: 'Celsius' },
                    ]}
                    width="141px"
                  />
                }
                loading={loading}
              />

              <Box sx={classes.formButtonContainer}>
                <Button variant="contained" type="submit" disabled={isSubmitting || !dirty}>
                  Confirm
                </Button>
                <Button onClick={() => setIsEditing(false)}>Cancel</Button>
              </Box>
            </Form>
          )}
        </Formik>
      </div>
    )

  return (
    <div style={classes.subsectionContainer}>
      <Subsection
        header="Notifications"
        content={
          <>
            <NotificationPreference label="Text" state={textPreference} />
            <NotificationPreference label="Phone" state={phonePreference} />
            <NotificationPreference label="Email" state={emailPreference} />
            <NotificationPreference label="Push" state={pushPreference} />
          </>
        }
        loading={loading}
      />

      <Subsection
        header="Language"
        content={
          <>
            {language ||
              (hasInternalUserAccess ? (
                <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setIsEditing(true)}>
                  Set language
                </Link>
              ) : (
                'N/A'
              ))}
          </>
        }
        loading={loading}
      />

      <Subsection
        header="Timezone"
        content={
          timezone ? (
            <Typography variant="body1">
              {timeZoneHeader} ({timeZoneSubtitle})
            </Typography>
          ) : hasInternalUserAccess ? (
            <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setIsEditing(true)}>
              Set timezone
            </Link>
          ) : (
            'N/A'
          )
        }
        loading={loading}
        loadingWidth={120}
      />

      <Subsection
        header="Date Format"
        content={
          dateFormat ||
          (hasInternalUserAccess ? (
            <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setIsEditing(true)}>
              Set date format
            </Link>
          ) : (
            'N/A'
          ))
        }
        loading={loading}
        loadingWidth={120}
      />

      <Subsection
        header="Time Format"
        content={
          timeFormat ||
          (hasInternalUserAccess ? (
            <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setIsEditing(true)}>
              Set time format
            </Link>
          ) : (
            'N/A'
          ))
        }
        loading={loading}
        loadingWidth={60}
      />

      <Subsection
        header="Temperature"
        content={
          tempUnits ||
          (hasInternalUserAccess ? (
            <Link component="button" variant="body1" color="primary" underline="hover" onClick={() => setIsEditing(true)}>
              Set temperature units
            </Link>
          ) : (
            'N/A'
          ))
        }
        loading={loading}
        loadingWidth={100}
      />
    </div>
  )
}

export default PreferencesContent
